import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useTheme } from 'styled-components';

import { Flex, Text } from 'src/packages/core';
import { DarkTheme, LightTheme, ThemeType } from 'src/packages/theme';
import { Announcement } from 'src/packages/common';
import { goToUrl } from 'src/utils/go-to-url';
import { config } from 'src/config';
import { sanitizeHtmlStyles } from 'src/utils/html-sanitizer';

import { NotificationsItemWrapper } from '../../Header.styles';

interface NotificationIconPopoverContentProps {
  theme: ThemeType;
  announcements: Announcement[];
}

export const Announcements = ({ theme, announcements }: NotificationIconPopoverContentProps) => {
  const { t } = useTranslation();
  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const menuItemBgHoverColor = defaultTheme.colors.background.secondary.BG4;

  return (
    <Flex flexDirection={'column'} width={'100%'}>
      {announcements.length > 0 ? (
        <>
          {announcements.slice(0, 5).map((item: Announcement, index) => {
            return (
              <NotificationsItemWrapper
                key={index}
                clickable={Boolean(item.directUrl)}
                menuItemBgHoverColor={menuItemBgHoverColor}
                onClick={() => item.directUrl && goToUrl(item.directUrl, '_blank')}
              >
                <Flex width={'100%'} flexDirection={'column'} gap={'4px'} py={4} px={5}>
                  <Text
                    variant={'p2'}
                    text={item.title}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.primary
                        : DarkTheme.colors.text.primary
                    }
                  />
                  <Text
                    variant={'p3'}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.tertiary
                        : DarkTheme.colors.text.tertiary
                    }
                    className='content'
                  >
                    <Flex dangerouslySetInnerHTML={{ __html: sanitizeHtmlStyles(item.content) }} />
                  </Text>
                  <Text
                    variant={'p3'}
                    text={`${moment(Number(item.publishTime)).format('YYYY-MM-DD HH:mm:ss')}`}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.tertiary
                        : DarkTheme.colors.text.tertiary
                    }
                  />
                </Flex>
              </NotificationsItemWrapper>
            );
          })}
          <Flex
            mt={3}
            py={4}
            px={5}
            onClick={() => {
              goToUrl(config.announcementsUrl);
            }}
            style={{ cursor: 'pointer' }}
          >
            <Text
              variant={'p2'}
              text={t('more')}
              color={contextTheme.colors.palette.primary}
              style={{ cursor: 'pointer' }}
            />
          </Flex>
        </>
      ) : (
        <Flex py={5} px={5}>
          <Text
            text={t('header.menu.notification_icon.no_new_announcements')}
            color={DarkTheme.colors.text.primary}
            textAlign={'center'}
            width={'100%'}
            variant={'p2'}
          />
        </Flex>
      )}
    </Flex>
  );
};
